import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { sectionsApi } from '@/api/sections'
import { usersApi } from '@/api/users'
import { useEffectiveRole } from '@/store/auth'
import { X, Plus, Trash2 } from 'lucide-react'
import type { SectionCreate, SectionStageCreate, DocType } from '@/types'
import { DOC_TYPE_LABELS } from '@/types'
import CharCounter from '@/components/common/CharCounter'
import MoneyInput from '@/components/common/MoneyInput'

interface Props {
  projectId: string
  onClose: () => void
  docType?: DocType
  parentId?: string | null
}

const today = new Date().toISOString().split('T')[0]

const schema = z
  .object({
    code:        z.string().min(1, 'Выберите раздел'),
    name:        z.string().max(300, 'Максимум 300 символов').optional(),
    description: z.string().max(500, 'Максимум 500 символов').optional(),
    assignee_id: z.string().optional(),
    planned_start: z.string().min(1, 'Укажите дату начала'),
    planned_end:   z.string().min(1, 'Укажите срок выдачи'),
    cost:        z.string().optional(),
    note:        z.string().max(1000, 'Максимум 1000 символов').optional(),
  })
  .refine((d) => d.planned_end > d.planned_start, {
    message: 'Срок выдачи должен быть позже даты начала',
    path: ['planned_end'],
  })

type FormData = z.infer<typeof schema>

type StageRow = {
  name: string
  start_date: string
  end_date: string
  cost: string
}

export default function AddSectionModal({ projectId, onClose, docType, parentId }: Props) {
  const qc = useQueryClient()
  const overlayRef = useRef<HTMLDivElement>(null)
  const [stages, setStages] = useState<StageRow[]>([])
  const role = useEffectiveRole()
  // Деньги видит и вводит только директор/зам (ГИП добавляет разделы без стоимостей)
  const showCost = role === 'director' || role === 'deputy_director'

  // Справочник кодов разделов (фильтруем по doc_type если задан)
  const { data: codes = [] } = useQuery({
    queryKey: ['section-codes', docType],
    queryFn: () => sectionsApi.getCodes(docType).then((r) => r.data),
  })

  // Список активных проектировщиков для назначения исполнителя
  const { data: users = [] } = useQuery({
    queryKey: ['users-assignable'],
    queryFn: () => usersApi.assignable().then((r) => r.data),
  })
  const assignees = users.filter((u) => u.role !== 'director' && u.is_active)

  const createMutation = useMutation({
    mutationFn: (data: SectionCreate) => sectionsApi.create(projectId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sections', projectId] })
      qc.invalidateQueries({ queryKey: ['gantt'] })
      qc.invalidateQueries({ queryKey: ['dashboard-zones'] })
      onClose()
    },
  })

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { planned_start: today },
  })

  const sectionStart = watch('planned_start')
  const sectionEnd = watch('planned_end')

  function stageDateOutOfRange(stage: StageRow): boolean {
    if (stage.start_date && sectionStart && stage.start_date < sectionStart) return true
    if (stage.end_date && sectionEnd && stage.end_date > sectionEnd) return true
    return false
  }

  // Закрытие по клику на фон
  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === overlayRef.current) onClose()
  }

  // Закрытие по Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  function addStage() {
    setStages((prev) => [
      ...prev,
      { name: `Этап ${prev.length + 1}`, start_date: '', end_date: '', cost: '' },
    ])
  }

  function removeStage(idx: number) {
    setStages((prev) => prev.filter((_, i) => i !== idx))
  }

  function updateStage(idx: number, field: keyof StageRow, value: string) {
    setStages((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s))
    )
  }

  function onSubmit(data: FormData) {
    const stagesPayload: SectionStageCreate[] = stages.map((s) => ({
      name: s.name,
      start_date: s.start_date || null,
      end_date: s.end_date || null,
      cost: showCost ? (s.cost || null) : null,
    }))

    const payload: SectionCreate = {
      code:          data.code,
      assignee_id:   data.assignee_id || undefined,
      planned_start: data.planned_start,
      planned_end:   data.planned_end,
      note:          data.note || undefined,
      doc_type:      docType ?? 'pd',
      parent_id:     parentId ?? undefined,
      name:          data.name || undefined,
      description:   data.description || undefined,
      cost:          showCost ? (data.cost || undefined) : undefined,
      stages:        stagesPayload,
    }
    createMutation.mutate(payload)
  }

  const inputCls =
    'block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500'
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1'
  const errorCls = 'mt-1 text-xs text-rose-600'

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
    >
      <div className="w-full max-w-xl mx-4 rounded-xl bg-white shadow-xl border border-gray-200 max-h-[90vh] overflow-y-auto">
        {/* Заголовок */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">
            Добавить раздел{docType ? ` - ${DOC_TYPE_LABELS[docType]}` : ''}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Форма */}
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="p-6 space-y-4">

          {/* Код раздела */}
          <div>
            <label className={labelCls}>{docType === 'pd' ? 'Раздел (шифр ПП-87) *' : 'Раздел (шифр) *'}</label>
            {codes.length > 0 ? (
              <select {...register('code')} className={inputCls}>
                <option value=""> - выберите раздел - </option>
                {codes.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.parent_code ? '\u00a0\u00a0\u00a0\u00a0' : ''}{c.number ? `№${c.number} ` : ''}{c.code}: {c.name}
                  </option>
                ))}
              </select>
            ) : (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                Нет доступных кодов раздела для этого типа документации.
                Обратитесь к администратору или выберите другой тип документации.
              </p>
            )}
            {errors.code && <p className={errorCls}>{errors.code.message}</p>}
          </div>

          {/* Наименование подраздела */}
          <div>
            <label className={labelCls}>Наименование подраздела</label>
            <input
              type="text"
              {...register('name')}
              maxLength={300}
              placeholder="Вводится вручную"
              className={inputCls}
            />
            {errors.name && <p className={errorCls}>{errors.name.message}</p>}
            <CharCounter value={watch('name')} max={300} />
          </div>

          {/* Наименование (пояснение) */}
          <div>
            <label className={labelCls}>Наименование (пояснение)</label>
            <input
              type="text"
              {...register('description')}
              maxLength={500}
              placeholder="Дополнительное пояснение"
              className={inputCls}
            />
            {errors.description && <p className={errorCls}>{errors.description.message}</p>}
            <CharCounter value={watch('description')} max={500} />
          </div>

          {/* Исполнитель */}
          <div>
            <label className={labelCls}>Исполнитель</label>
            <select {...register('assignee_id')} className={inputCls}>
              <option value=""> - не назначен - </option>
              {assignees.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                </option>
              ))}
            </select>
          </div>

          {/* Даты */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Дата начала *</label>
              <input type="date" {...register('planned_start')} className={inputCls} />
              {errors.planned_start && <p className={errorCls}>{errors.planned_start.message}</p>}
              <p className="mt-1 text-xs text-gray-400">Дата начала работ по разделу</p>
            </div>
            <div>
              <label className={labelCls}>Срок выдачи *</label>
              <input type="date" {...register('planned_end')} className={inputCls} />
              {errors.planned_end && <p className={errorCls}>{errors.planned_end.message}</p>}
              <p className="mt-1 text-xs text-gray-400">Не раньше даты начала</p>
            </div>
          </div>

          {/* Стоимость раздела — только директор/зам */}
          {showCost && (
            <div>
              <label className={labelCls}>Стоимость раздела, ₽</label>
              <Controller
                name="cost"
                control={control}
                render={({ field }) => (
                  <MoneyInput
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="например 1 500 000"
                    className={inputCls}
                  />
                )}
              />
              <p className="mt-1 text-xs text-gray-400">Стоимость без НДС, руб.</p>
            </div>
          )}

          {/* Примечание */}
          <div>
            <label className={labelCls}>Примечание</label>
            <textarea
              {...register('note')}
              rows={2}
              maxLength={1000}
              placeholder="Необязательный комментарий..."
              className={`${inputCls} resize-none`}
            />
            {errors.note && <p className={errorCls}>{errors.note.message}</p>}
            <CharCounter value={watch('note')} max={1000} />
          </div>

          {/* Этапы раздела */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-gray-700">Этапы</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            {stages.length === 0 ? (
              <p className="text-xs text-gray-400 mb-2">Этапы не добавлены - раздел будет создан без разбивки на этапы</p>
            ) : (
              <div className="space-y-2 mb-2">
                {stages.map((stage, idx) => (
                  <div key={idx} className={`rounded-lg border p-3 ${
                    stageDateOutOfRange(stage) ? 'border-rose-300 bg-rose-50' : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-gray-500 w-14 shrink-0">Этап {idx + 1}</span>
                      <input
                        type="text"
                        value={stage.name}
                        onChange={(e) => updateStage(idx, 'name', e.target.value)}
                        placeholder={`Этап ${idx + 1}`}
                        className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                      <button
                        type="button"
                        onClick={() => removeStage(idx)}
                        className="rounded p-1 text-gray-400 hover:text-rose-500 hover:bg-rose-50 transition-colors"
                        title="Удалить этап"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className={`grid ${showCost ? 'grid-cols-3' : 'grid-cols-2'} gap-2`}>
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Начало</label>
                        <input
                          type="date"
                          value={stage.start_date}
                          onChange={(e) => updateStage(idx, 'start_date', e.target.value)}
                          className="block w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Окончание</label>
                        <input
                          type="date"
                          value={stage.end_date}
                          onChange={(e) => updateStage(idx, 'end_date', e.target.value)}
                          className="block w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                        <p className="mt-0.5 text-xs text-gray-400">≥ начала</p>
                      </div>
                      {showCost && (
                        <div>
                          <label className="block text-xs text-gray-500 mb-0.5">Стоимость, руб.</label>
                          <MoneyInput
                            value={stage.cost}
                            onChange={(v) => updateStage(idx, 'cost', v)}
                            placeholder="0"
                            className="block w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                          />
                        </div>
                      )}
                    </div>
                    {stageDateOutOfRange(stage) && (
                      <p className="mt-1 text-xs text-rose-600">
                        Даты этапа должны быть в пределах дат раздела ({sectionStart || '…'} - {sectionEnd || '…'})
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={addStage}
              className="flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700 font-medium"
            >
              <Plus className="h-4 w-4" />
              Добавить этап
            </button>
          </div>

          {/* Ошибка сервера */}
          {createMutation.isError && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">
              Ошибка при добавлении раздела. Попробуйте ещё раз.
            </div>
          )}

          {/* Кнопки */}
          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={isSubmitting || createMutation.isPending || codes.length === 0}
              className="flex-1 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {createMutation.isPending ? 'Добавление...' : 'Добавить'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
